import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense.ts";
import { useBoolean } from "@/hooks/use-boolean.ts";
import useUpdatePortalCustomText from "@babackapp/private-api-client/client/use-update-portal-custom-text";
import { Button } from "@babackapp/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@babackapp/ui/components/dialog";
import { PropsWithChildren, useMemo, useState } from "react";
import { TranslatePortalCustomTextButton } from "./translate-portal-custom-text-button.tsx";
import { useTranslationDefaultResolver, useTranslationMultiLangSearch } from "./use-translation-defaults.ts";
import type { WidgetKeyDescriptor } from "./widget-keys.ts";
import type { PortalCustomTextEntry } from "./types.ts";

type CustomTextEntry = PortalCustomTextEntry;

type FormValue = { lang: string; text: string };

type Props = {
  descriptor: WidgetKeyDescriptor;
  override?: CustomTextEntry;
  disabled?: boolean;
};

export function PortalWordingDrawer({
  descriptor,
  override,
  disabled,
  children,
}: PropsWithChildren<Props>) {
  const open = useBoolean(false);
  const { data: shop } = useCurrentShopSuspense();
  const languages = shop.preferences.languages as string[];
  const { mutateAsync, isPending } = useUpdatePortalCustomText(shop);
  const { resolve } = useTranslationDefaultResolver("en");

  const FULL_LOCALE_CODES = new Set(["pt-br", "pt-pt", "zh-hans", "zh-hant"]);
  const langCodes = useMemo(
    () =>
      languages.map((l) => {
        const lower = l.toLowerCase();
        return FULL_LOCALE_CODES.has(lower) ? lower : lower.split("-")[0];
      }),
    [languages],
  );
  const { resolveForLang } = useTranslationMultiLangSearch(langCodes);

  const initialValues: FormValue[] = useMemo(() => {
    const existing = new Map(
      (override?.texts ?? []).map((t) => [t.lang, t.text]),
    );
    return languages.map((lang) => ({
      lang,
      text: existing.get(lang) ?? "",
    }));
  }, [override, languages]);

  const [values, setValues] = useState<FormValue[]>(initialValues);

  const reset = () => setValues(initialValues);

  const onOpenChange = (next: boolean) => {
    if (next) reset();
    open.setValue(next);
  };

  const onSave = async () => {
    const clean = values
      .map((v) => ({ lang: v.lang, text: v.text }))
      .filter((v) => v.text.trim().length > 0);
    await mutateAsync({
      key: descriptor.key,
      texts: clean,
    });
    open.setValue(false);
  };

  const onRemoveAll = async () => {
    await mutateAsync({
      key: descriptor.key,
      texts: [],
    });
    open.setValue(false);
  };

  const onValueChange = (lang: string, text: string) => {
    setValues((prev) =>
      prev.map((v) => (v.lang === lang ? { ...v, text } : v)),
    );
  };

  const defaultText = resolve(descriptor.key);

  return (
    <Dialog open={open.value} onOpenChange={onOpenChange}>
      <DialogTrigger disabled={disabled} asChild>
        {children}
      </DialogTrigger>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {descriptor.label}
          </DialogTitle>
          <DialogDescription className="flex flex-col gap-1">
            <code className="text-xs font-mono bg-muted px-2 py-1 rounded self-start">
              {descriptor.key}
            </code>
            {defaultText ? (
              <span className="text-xs text-muted-foreground mt-2 whitespace-pre-wrap">
                Texte par défaut (EN) : {defaultText}
              </span>
            ) : null}
          </DialogDescription>
        </DialogHeader>

        <div className="flex justify-end">
          <TranslatePortalCustomTextButton
            shopId={shop.id}
            values={values}
            languages={languages}
            onTranslated={(translated) => {
              setValues((prev) =>
                prev.map((v) =>
                  translated[v.lang] != null
                    ? { ...v, text: translated[v.lang] }
                    : v,
                ),
              );
            }}
          />
        </div>

        <div className="flex flex-col gap-4">
          {values.map((v) => {
            const langCode = FULL_LOCALE_CODES.has(v.lang)
              ? v.lang
              : v.lang.split("-")[0];
            const placeholder =
              resolveForLang(descriptor.key, langCode) ?? defaultText ?? "";
            return (
              <label key={v.lang} className="flex flex-col gap-1.5">
                <span className="text-xs font-semibold text-muted-foreground uppercase">
                  {v.lang}
                </span>
                <textarea
                  value={v.text}
                  onChange={(e) => onValueChange(v.lang, e.target.value)}
                  rows={3}
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                  placeholder={placeholder}
                />
              </label>
            );
          })}
        </div>

        <div className="flex items-center justify-between gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onRemoveAll}
            disabled={isPending || !override}
          >
            Revenir au texte par défaut
          </Button>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => open.setValue(false)}
              disabled={isPending}
            >
              Annuler
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={onSave}
              disabled={isPending}
            >
              Enregistrer
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
